import { easterSunday } from "./calendar";
import { addDaysIso, fmtDateDE } from "./finance";

// Gesetzliche Feiertage Italien (national). Quellen: Legge 27.05.1949 n. 260,
// Legge 05.03.1977 n. 54, DPR 28.12.1985 n. 792, Legge 20.11.2000 n. 336 (2. Juni),
// Legge 08.10.2025 n. 151 (San Francesco d'Assisi, 04.10., ab 2026).
// Ostermontag beweglich (Gregorianische Osterformel in calendar.js).
export function italianNationalHolidays(year) {
  const e = easterSunday(year);
  const em = new Date(e.getFullYear(), e.getMonth(), e.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  const days = [
    `${year}-01-01`, `${year}-01-06`,
    `${em.getFullYear()}-${pad(em.getMonth() + 1)}-${pad(em.getDate())}`,
    `${year}-04-25`, `${year}-05-01`, `${year}-06-02`, `${year}-08-15`,
    `${year}-11-01`, `${year}-12-08`, `${year}-12-25`, `${year}-12-26`,
  ];
  // San Francesco d'Assisi: nationaler Feiertag ab 2026 (Legge n. 151/2025).
  if (year >= 2026) days.push(`${year}-10-04`);
  return new Set(days);
}

function isWorkday(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  if (dow === 0 || dow === 6) return false;
  return !italianNationalHolidays(y).has(iso);
}

export function nextWorkday(iso) {
  let cur = iso;
  while (cur && !isWorkday(cur)) cur = addDaysIso(cur, 1);
  return cur;
}

function addWorkdays(iso, n) {
  let cur = iso;
  let left = n;
  while (left > 0) {
    cur = addDaysIso(cur, 1);
    if (isWorkday(cur)) left -= 1;
  }
  return cur;
}

export const REMINDER_STAGES = ["due", "r1", "r2", "r3", "m1", "pec"];

const STAGE_META = {
  due: { title: "Fälligkeit", icon: "warning", planner: true },
  r1:  { title: "1. Zahlungserinnerung", icon: "payment_reminder", planner: true },
  r2:  { title: "2. Zahlungserinnerung", icon: "payment_reminder", planner: true },
  r3:  { title: "3. Zahlungserinnerung", icon: "payment_reminder", planner: true },
  m1:  { title: "1. Mahnung", icon: "payment_reminder", planner: false },
  pec: { title: "2. Mahnung per PEC", icon: "email", planner: false },
};

// Zahlungsfrist der 1. Mahnung: 7 Kalendertage ab (Versand- oder) Mahntermin, auf Arbeitstag verschoben.
export function reminderDeadline(m1Iso) {
  return m1Iso ? nextWorkday(addDaysIso(m1Iso, 7)) : "";
}

export function computeReminderChain(dueIso, m1SentIso) {
  if (!dueIso) return null;
  const r1 = addWorkdays(dueIso, 3);
  const r2 = nextWorkday(addDaysIso(r1, 4));
  const r3 = nextWorkday(addDaysIso(r2, 7));
  const m1 = nextWorkday(addDaysIso(r3, 7));
  const deadline = reminderDeadline(m1SentIso || m1);
  const pec = nextWorkday(addDaysIso(deadline, 3));
  return { due: dueIso, r1, r2, r3, m1, deadline, pec };
}

export function isInvoiceActive(pay) {
  return Boolean(pay && (pay.status === "invoiced" || pay.status === "due") && pay.due_date);
}

export function removeOpenAutoNotes(notes, ref) {
  return ref ? notes.filter((n) => !(n.auto_ref === ref && !n.done_date)) : notes;
}

function buildNote(stage, ref, label, dateIso) {
  const meta = STAGE_META[stage];
  return {
    title: `${meta.title} – ${label}`,
    date: dateIso,
    show_in_planner: meta.planner,
    planner_icon: meta.icon,
    auto_ref: ref,
    auto_stage: stage,
    done_date: "",
  };
}

// Reagiert auf eine Zahlungsänderung (prev → next) und liefert die neue Notizliste.
export function reconcileAutoNotes(notes, prevPay, nextPay, label) {
  const ref = nextPay?.ref || prevPay?.ref;
  if (!ref) return notes;
  const linked = (n) => n.auto_ref === ref;
  const open = (n) => linked(n) && !n.done_date;
  const wasActive = isInvoiceActive(prevPay);
  const nowActive = isInvoiceActive(nextPay);

  if (nextPay.status === "paid" || (wasActive && !nowActive)) {
    return notes.filter((n) => !open(n));
  }
  if (!nowActive) return notes;

  const m1Sent = notes.find((n) => linked(n) && n.auto_stage === "m1")?.done_date || "";
  const chain = computeReminderChain(nextPay.due_date, m1Sent);

  if (!wasActive) {
    const existing = new Set(notes.filter(linked).map((n) => n.auto_stage));
    const added = REMINDER_STAGES.filter((s) => !existing.has(s)).map((s) =>
      buildNote(s, ref, label || "Zahlung", chain[s]),
    );
    return [...notes, ...added];
  }
  if (prevPay.due_date !== nextPay.due_date) {
    return notes.map((n) => (open(n) && chain[n.auto_stage] ? { ...n, date: chain[n.auto_stage] } : n));
  }
  return notes;
}

// Mahnversand erfasst → PEC-Termin (falls offen) neu berechnen.
export function applyM1Sent(notes, ref, sentIso) {
  return notes.map((n) => {
    if (n.auto_ref !== ref || n.auto_stage !== "pec" || n.done_date) return n;
    const m1 = notes.find((x) => x.auto_ref === ref && x.auto_stage === "m1");
    const base = sentIso || m1?.date || "";
    return base ? { ...n, date: nextWorkday(addDaysIso(reminderDeadline(base), 3)) } : n;
  });
}

// Offene automatische Einträge ohne zugehörige Zahlung entfernen.
export function dropOrphanAutoNotes(notes, refs) {
  const set = new Set(refs.filter(Boolean));
  return notes.filter((n) => !n.auto_ref || n.done_date || set.has(n.auto_ref));
}

export function deadlineLabel(note) {
  if (note?.auto_stage !== "m1") return "";
  const d = reminderDeadline(note.done_date || note.date);
  return d ? `Zahlungsfrist bis ${fmtDateDE(d)}` : "";
}
